<template>
  <v-container>
    <v-row>
      <v-col cols="12">
        <div class="d-flex justify-space-between align-center mb-4">
          <h1 class="text-h4 mb-0">{{ $t('settlements.title') }}</h1>
          <v-btn color="primary" @click="openAddDialog">{{ $t('settlements.add') }}</v-btn>
        </div>
      </v-col>
    </v-row>
    
    <!-- Filters -->
    <v-row>
      <v-col cols="12">
        <div class="filters-wrapper">
          <v-toolbar class="mb-0">
            <v-toolbar-title class="text-subtitle-1 mr-4">{{ $t('settlements.filters') }}:</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-select
              v-model="filterInvestor"
              :items="investors"
              item-title="name"
              item-value="id"
              :label="$t('settlements.investorColumn')"
              clearable
              density="compact"
              variant="outlined"
              hide-details
              class="mr-3"
              style="max-width: 250px;"
            />
            <v-autocomplete
              v-model="filterWallet"
              v-model:search="walletFilterSearch"
              :items="filteredWalletsForFilter"
              item-title="display_name"
              item-value="id"
              :label="$t('settlements.walletColumn')"
              :placeholder="$t('settlements.walletSearchPlaceholder')"
              clearable
              density="compact"
              variant="outlined"
              hide-details
              auto-select-first
              class="mr-2"
              style="max-width: 250px;"
              @update:model-value="(val) => {
                if (!val) {
                  walletFilterSearch = ''
                }
              }"
            >
              <template #no-data>
                <div class="pa-2">{{ $t('settlements.noWalletsFound') }}</div>
              </template>
            </v-autocomplete>
            <v-select
              v-model="filterDirection"
              :items="directionOptions"
              item-title="label"
              item-value="value"
              :label="$t('settlements.direction')"
              clearable
              density="compact"
              variant="outlined"
              hide-details
              class="mr-2"
              style="max-width: 250px;"
            />
            <div class="date-from-wrapper mr-2">
              <v-text-field
                v-model="filterDateFrom"
                :label="$t('settlements.dateFrom')"
                type="date"
                density="compact"
                variant="outlined"
                hide-details
                clearable
                style="max-width: 160px;"
              />
            </div>
            <v-text-field
              v-model="filterDateTo"
              :label="$t('settlements.dateTo')"
              type="date"
              density="compact"
              variant="outlined"
              hide-details
              clearable
              class="mr-2"
              style="max-width: 160px;"
            />
          </v-toolbar>
          <div class="period-buttons-container">
            <v-btn-toggle v-model="datePeriod" mandatory class="period-buttons">
              <v-btn value="day" size="small">{{ $t('settlements.day') }}</v-btn>
              <v-btn value="week" size="small">{{ $t('settlements.week') }}</v-btn>
              <v-btn value="month" size="small">{{ $t('settlements.month') }}</v-btn>
            </v-btn-toggle>
          </div>
        </div>
      </v-col>
    </v-row>
    
    <v-row>
      <v-col cols="12">
            <v-table density="compact" class="no-border-table">
              <thead>
                <tr>
                  <th 
                    class="px-4 py-3 sortable-header" 
                    :class="{ 'sort-active': sortBy === 'transaction_date' }"
                    style="cursor: pointer; user-select: none;"
                    @click="handleSort('transaction_date')"
                  >
                    <div class="d-flex align-center">
                      {{ $t('settlements.dateColumn') }}
                      <v-icon size="small" class="ml-1 sort-icon">{{ getSortIcon('transaction_date') }}</v-icon>
                    </div>
                  </th>
                  <th 
                    class="px-4 py-3 sortable-header" 
                    :class="{ 'sort-active': sortBy === 'investor' }"
                    style="cursor: pointer; user-select: none;"
                    @click="handleSort('investor')"
                  >
                    <div class="d-flex align-center">
                      {{ $t('settlements.investorColumn') }}
                      <v-icon size="small" class="ml-1 sort-icon">{{ getSortIcon('investor') }}</v-icon>
                    </div>
                  </th>
                  <th 
                    class="px-4 py-3 sortable-header" 
                    :class="{ 'sort-active': sortBy === 'wallet' }"
                    style="cursor: pointer; user-select: none;"
                    @click="handleSort('wallet')"
                  >
                    <div class="d-flex align-center">
                      {{ $t('settlements.walletColumn') }}
                      <v-icon size="small" class="ml-1 sort-icon">{{ getSortIcon('wallet') }}</v-icon>
                    </div>
                  </th>
                  <th 
                    class="px-4 py-3 sortable-header" 
                    :class="{ 'sort-active': sortBy === 'direction' }"
                    style="cursor: pointer; user-select: none;"
                    @click="handleSort('direction')"
                  >
                    <div class="d-flex align-center">
                      {{ $t('settlements.directionColumn') }}
                      <v-icon size="small" class="ml-1 sort-icon">{{ getSortIcon('direction') }}</v-icon>
                    </div>
                  </th>
                  <th 
                    class="px-4 py-3 sortable-header" 
                    :class="{ 'sort-active': sortBy === 'amount' }"
                    style="cursor: pointer; user-select: none;"
                    @click="handleSort('amount')"
                  >
                    <div class="d-flex align-center">
                      {{ $t('settlements.amountColumn') }}
                      <v-icon size="small" class="ml-1 sort-icon">{{ getSortIcon('amount') }}</v-icon>
                    </div>
                  </th>
                  <th 
                    class="px-4 py-3 sortable-header" 
                    :class="{ 'sort-active': sortBy === 'amount_usdt' }"
                    style="cursor: pointer; user-select: none;"
                    @click="handleSort('amount_usdt')"
                  >
                    <div class="d-flex align-center">
                      {{ $t('settlements.amountUsdtColumn') }}
                      <v-icon size="small" class="ml-1 sort-icon">{{ getSortIcon('amount_usdt') }}</v-icon>
                    </div>
                  </th>
                  <th 
                    class="px-4 py-3 sortable-header" 
                    :class="{ 'sort-active': sortBy === 'rate' }"
                    style="cursor: pointer; user-select: none;"
                    @click="handleSort('rate')"
                  >
                    <div class="d-flex align-center">
                      {{ $t('settlements.rateColumn') }}
                      <v-icon size="small" class="ml-1 sort-icon">{{ getSortIcon('rate') }}</v-icon>
                    </div>
                  </th>
                  <th 
                    class="px-4 py-3 sortable-header" 
                    :class="{ 'sort-active': sortBy === 'user' }"
                    style="cursor: pointer; user-select: none;"
                    @click="handleSort('user')"
                  >
                    <div class="d-flex align-center">
                      {{ $t('settlements.userColumn') }}
                      <v-icon size="small" class="ml-1 sort-icon">{{ getSortIcon('user') }}</v-icon>
                    </div>
                  </th>
                  <th class="px-4 py-3">{{ $t('settlements.descriptionColumn') }}</th>
                  <th class="px-4 py-3">{{ $t('common.actions') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="settlement in filteredSettlements" :key="settlement.id">
                  <td>{{ formatDateForDisplay(settlement.transaction_date) }}</td>
                  <td>{{ settlement.investor_name }}</td>
                  <td>{{ settlement.wallet_display }}</td>
                  <td>{{ settlement.direction === 'to_wallet' ? $t('settlements.toWallet') : $t('settlements.toInvestor') }}</td>
                  <td class="text-right">{{ formatAmount(settlement.amount) }}</td>
                  <td class="text-right">{{ formatAmount(settlement.amount_usdt) }}</td>
                  <td class="text-right">{{ formatAmount(settlement.rate) }}</td>
                  <td>{{ settlement.user_email || '-' }}</td>
                  <td>{{ settlement.description || '-' }}</td>
                  <td class="pa-0 text-center">
                    <v-btn
                      variant="plain"
                      density="compact"
                      size="small"
                      :title="$t('walletOperations.linkTitle')"
                      class="pa-0"
                      icon="mdi-format-list-bulleted"
                      @click="$router.push({ path: '/wallet-operations', query: { document_type: 'Settlement', document_id: settlement.id } })"
                    ></v-btn>
                    <v-btn variant="plain" density="compact" size="small" @click="editSettlement(settlement)" icon="mdi-pencil" class="pa-0"></v-btn>
                    <v-btn variant="plain" density="compact" size="small" color="error" @click="deleteSettlement(settlement.id)" class="pa-0" icon="mdi-delete"></v-btn>
                  </td>
                </tr>
                <tr v-if="filteredSettlements.length === 0">
                  <td colspan="10" class="text-center text-grey">{{ $t('settlements.noSettlementsFound') }}</td>
                </tr>
              </tbody>
              <tfoot>
                <tr class="total-row">
                  <td colspan="5" class="text-right font-weight-bold">{{ $t('settlements.total') }}</td>
                  <td class="text-right font-weight-bold">{{ formattedTotalSettlements }}</td>
                  <td colspan="4"></td>
                </tr>
              </tfoot>
            </v-table>
      </v-col>
    </v-row>

    <SettlementEditDialog
      v-model="showAddDialog"
      :settlement="editingSettlement"
      :investors="investors"
      :wallets="wallets"
      :users="users"
      @save="handleSaveSettlement"
      @cancel="handleCancelSettlement"
    />

    <v-dialog v-model="showErrorDialog" max-width="500">
      <v-card>
        <v-card-title class="text-h6 text-error">
          <v-icon color="error" class="mr-2">mdi-alert-circle</v-icon>
          {{ $t('errors.unknownError') }}
        </v-card-title>
        <v-card-text>
          <p class="text-body-1">{{ errorMessage }}</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="showErrorDialog = false">{{ $t('errors.ok') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { apiGet, apiPost, apiPut, apiDelete, getErrorMessage } from '../utils/api'
import { getDateRangeForPeriod, getStartOfDay, getEndOfDay, formatDateForDisplay, getTodayString, formatDateToYYYYMMDD } from '../utils/date'
import SettlementEditDialog from './dialogs/SettlementEditDialog.vue'

const { t } = useI18n()

interface Investor {
  id: number
  name: string
}

interface Wallet {
  id: number
  display_name: string
  usdt_rate?: number
  exchange?: {
    id: number
    name: string
  } | null
  coin: {
    id: number
    name: string
    symbol: string
  }
}

interface User {
  id: number
  email: string
}

interface Settlement {
  id: number
  investor_id: number
  investor_name: string
  wallet_id: number
  wallet_display: string
  user_id: number
  user_email?: string
  amount: number
  amount_usdt: number
  rate: number
  direction: string
  transaction_date: string
  description: string | null
}

const settlements = ref<Settlement[]>([])
const investors = ref<Investor[]>([])
const wallets = ref<Wallet[]>([])
const users = ref<User[]>([])
const showAddDialog = ref(false)
const editingSettlement = ref<Settlement | null>(null)
const showErrorDialog = ref(false)
const errorMessage = ref('')
const walletSearch = ref('')
const walletFilterSearch = ref('')

const filterInvestor = ref<number | null>(null)
const filterWallet = ref<number | null>(null)
const filterDirection = ref<string | null>(null)
const filterDateFrom = ref<string | null>(null)
const filterDateTo = ref<string | null>(null)
const datePeriod = ref<string>('day')

// Sort state - default to date ascending
const sortBy = ref<string>('transaction_date')
const sortOrder = ref<'asc' | 'desc'>('asc')

const directionOptions = computed(() => [
  { label: t('settlements.toWallet'), value: 'to_wallet' },
  { label: t('settlements.toInvestor'), value: 'to_investor' }
])

const settlementForm = ref({
  id: null as number | null,
  investor_id: null as number | null,
  wallet_id: null as number | null,
  user_id: null as number | null,
  amount: 0,
  direction: 'to_wallet',
  transaction_date: getTodayString(),
  description: ''
})

const loadSettlements = async () => {
  try {
    const response = await apiGet('/api/settlements')
    if (response.ok) {
      settlements.value = await response.json()
    }
  } catch (error) {
    console.error('Load settlements error:', error)
  }
}

const loadInvestors = async () => {
  try {
    const response = await apiGet('/api/investors')
    if (response.ok) {
      investors.value = await response.json()
    }
  } catch (error) {
    console.error('Load investors error:', error)
  }
}

const loadWallets = async () => {
  try {
    const response = await apiGet('/api/wallets')
    if (response.ok) {
      const walletData = await response.json()
      wallets.value = walletData.map((w: any) => {
        const exchangeName = w.exchange?.name || ''
        const coinSymbol = w.coin?.symbol || ''
        const displayName = exchangeName ? `${exchangeName} - ${coinSymbol}` : coinSymbol
        return {
          ...w,
          display_name: displayName,
          usdt_rate: w.usdt_rate || 0
        }
      })
    }
  } catch (error) {
    console.error('Load wallets error:', error)
  }
}

const loadUsers = async () => {
  try {
    const response = await apiGet('/api/users')
    if (response.ok) {
      users.value = await response.json()
    }
  } catch (error) {
    console.error('Load users error:', error)
  }
}

const loadCurrentUser = async () => {
  try {
    const response = await apiGet('/api/users/current')
    if (response.ok) {
      const userData = await response.json()
      return userData.id
    }
  } catch (error) {
    console.error('Load current user error:', error)
  }
  return null
}

// Fetch rate from database for a coin
// If created_at is provided, fetches the latest rate before or equal to that date
// Otherwise, fetches the absolute latest rate
const fetchRateFromDB = async (coinId: number | null, created_at?: string | null) => {
  if (!coinId) return null
  
  try {
    let url = `/api/coin_rates/latest?coin_id=${coinId}`
    if (created_at) {
      url += `&date_before=${created_at}`
    }
    const response = await apiGet(url)
    if (response.ok) {
      const rateData = await response.json()
      return rateData.rate
    }
  } catch (error) {
    console.error('Fetch rate from DB error:', error)
  }
  return null
}

const calculateAmountUsdt = () => {
  if (settlementForm.value.amount && settlementForm.value.rate) {
    const amount = parseFloat(settlementForm.value.amount?.toString() || '0')
    const rate = parseFloat(settlementForm.value.rate?.toString() || '0')
    if (!isNaN(amount) && !isNaN(rate) && amount > 0 && rate > 0) {
      settlementForm.value.amount_usdt = Math.round(amount * rate * 100000000) / 100000000
    } else {
      settlementForm.value.amount_usdt = 0
    }
  } else {
    settlementForm.value.amount_usdt = 0
  }
}

const filteredWallets = computed(() => {
  if (!wallets.value || wallets.value.length === 0) return []
  if (!walletSearch.value) return wallets.value
  const search = walletSearch.value.toLowerCase()
  return wallets.value.filter(wallet => {
    const displayName = wallet.display_name || (wallet.exchange?.name ? `${wallet.exchange.name} - ${wallet.coin?.symbol}` : wallet.coin?.symbol) || ''
    return displayName.toLowerCase().includes(search) ||
      wallet.exchange?.name?.toLowerCase().includes(search) ||
      wallet.coin?.symbol?.toLowerCase().includes(search) ||
      wallet.coin?.name?.toLowerCase().includes(search)
  })
})

const filteredWalletsForFilter = computed(() => {
  if (!wallets.value || wallets.value.length === 0) return []
  if (!walletFilterSearch.value) return wallets.value
  const search = walletFilterSearch.value.toLowerCase()
  return wallets.value.filter(wallet => {
    const displayName = wallet.display_name || (wallet.exchange?.name ? `${wallet.exchange.name} - ${wallet.coin?.symbol}` : wallet.coin?.symbol) || ''
    return displayName.toLowerCase().includes(search) ||
      wallet.exchange?.name?.toLowerCase().includes(search) ||
      wallet.coin?.symbol?.toLowerCase().includes(search) ||
      wallet.coin?.name?.toLowerCase().includes(search)
  })
})

// Set date period
const setDatePeriod = (period: string) => {
  const dateRange = getDateRangeForPeriod(period)
  filterDateFrom.value = dateRange.from
  filterDateTo.value = dateRange.to
}

// Watch date period changes
watch(datePeriod, (newPeriod) => {
  setDatePeriod(newPeriod)
})

const filteredSettlements = computed(() => {
  let filtered = settlements.value

  if (filterInvestor.value !== null) {
    filtered = filtered.filter(s => s.investor_id === filterInvestor.value)
  }

  if (filterWallet.value !== null) {
    filtered = filtered.filter(s => s.wallet_id === filterWallet.value)
  }

  if (filterDirection.value !== null) {
    filtered = filtered.filter(s => s.direction === filterDirection.value)
  }

  if (filterDateFrom.value) {
    const fromDate = getStartOfDay(filterDateFrom.value)
    filtered = filtered.filter(s => {
      const settlementDate = getStartOfDay(s.transaction_date)
      return settlementDate >= fromDate
    })
  }

  if (filterDateTo.value) {
    const toDate = getEndOfDay(filterDateTo.value)
    filtered = filtered.filter(s => {
      const settlementDate = new Date(s.transaction_date)
      return settlementDate <= toDate
    })
  }

  // Sort settlements
  const sorted = [...filtered].sort((a, b) => {
    let aValue: any
    let bValue: any

    switch (sortBy.value) {
      case 'transaction_date':
        aValue = new Date(a.transaction_date).getTime()
        bValue = new Date(b.transaction_date).getTime()
        break
      case 'investor':
        aValue = a.investor_name || ''
        bValue = b.investor_name || ''
        break
      case 'wallet':
        aValue = a.wallet_display || ''
        bValue = b.wallet_display || ''
        break
      case 'direction':
        aValue = a.direction || ''
        bValue = b.direction || ''
        break
      case 'amount':
        aValue = a.amount || 0
        bValue = b.amount || 0
        break
      case 'amount_usdt':
        aValue = parseFloat(a.amount_usdt?.toString() || '0') || 0
        bValue = parseFloat(b.amount_usdt?.toString() || '0') || 0
        break
      case 'rate':
        aValue = parseFloat(a.rate?.toString() || '0') || 0
        bValue = parseFloat(b.rate?.toString() || '0') || 0
        break
      case 'user':
        aValue = a.user_email || ''
        bValue = b.user_email || ''
        break
      default:
        return 0
    }

    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortOrder.value === 'asc' 
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue)
    } else {
      return sortOrder.value === 'asc'
        ? (aValue > bValue ? 1 : aValue < bValue ? -1 : 0)
        : (aValue < bValue ? 1 : aValue > bValue ? -1 : 0)
    }
  })

  return sorted
})

// Calculate total settlements from filtered settlements (using amount_usdt)
const totalSettlements = computed((): number => {
  return filteredSettlements.value.reduce((sum, settlement) => {
    return sum + (parseFloat(settlement.amount_usdt?.toString() || '0') || 0)
  }, 0)
})

const formattedTotalSettlements = computed(() => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(totalSettlements.value)
})

// Sort handler
const handleSort = (column: string) => {
  if (sortBy.value === column) {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortBy.value = column
    sortOrder.value = 'asc'
  }
}

const getSortIcon = (column: string) => {
  if (sortBy.value !== column) return 'mdi-sort'
  return sortOrder.value === 'asc' ? 'mdi-sort-ascending' : 'mdi-sort-descending'
}

// Format amount to 2 decimal places
const formatAmount = (amount: number | null | undefined): string => {
  const value = typeof amount === 'number' ? amount : parseFloat(String(amount || 0))
  if (isNaN(value)) return '0.00'
  return value.toFixed(2)
}

const openAddDialog = async () => {
  const userId = await loadCurrentUser()
  editingSettlement.value = {
    id: 0,
    investor_id: 0,
    investor_name: '',
    wallet_id: 0,
    wallet_display: '',
    user_id: userId || 0,
    user_email: '',
    amount: 0,
    amount_usdt: 0,
    rate: 0,
    direction: 'to_wallet',
    transaction_date: getTodayString(),
    description: null
  } as Settlement
  // Ensure wallets are loaded
  if (wallets.value.length === 0) {
    await loadWallets()
  }
  showAddDialog.value = true
}

const editSettlement = (settlement: Settlement) => {
  editingSettlement.value = settlement
  showAddDialog.value = true
}

const handleSaveSettlement = async (formData: any) => {
  if (!formData.investor_id || !formData.wallet_id || !formData.user_id || !formData.amount || !formData.direction || !formData.transaction_date) {
    showError(t('settlements.fillAllFields'))
    return
  }

  // Ensure rate and amount_usdt are set
  if (!formData.rate || formData.rate <= 0) {
    showError(t('settlements.setValidRate'))
    return
  }

  // Recalculate amount_usdt if needed
  if (!formData.amount_usdt || formData.amount_usdt <= 0) {
    const amount = parseFloat(formData.amount?.toString() || '0')
    const rate = parseFloat(formData.rate?.toString() || '0')
    if (!isNaN(amount) && !isNaN(rate) && amount > 0 && rate > 0) {
      formData.amount_usdt = Math.round(amount * rate * 100000000) / 100000000
    } else {
      showError(t('settlements.setValidAmountRate'))
      return
    }
  }

  try {
    const settlementData = {
      investor_id: formData.investor_id,
      wallet_id: formData.wallet_id,
      user_id: formData.user_id,
      amount: parseFloat(formData.amount?.toString() || '0'),
      amount_usdt: parseFloat(formData.amount_usdt?.toString() || '0'),
      rate: parseFloat(formData.rate?.toString() || '0'),
      direction: formData.direction,
      transaction_date: formData.transaction_date,
      description: formData.description || null
    }

    const url = formData.id ? `/api/settlements/${formData.id}` : '/api/settlements'
    const response = formData.id
      ? await apiPut(url, { settlement: settlementData })
      : await apiPost(url, { settlement: settlementData })

    if (response.ok) {
      await loadSettlements()
      showAddDialog.value = false
      editingSettlement.value = null
    } else {
      const errorText = await getErrorMessage(response, t('errors.unknownError'))
      showError(t('settlements.failedToSave', { errors: errorText }))
    }
  } catch (error) {
    console.error('Save settlement error:', error)
    showError(t('settlements.errorSaving'))
  }
}

const handleCancelSettlement = () => {
  editingSettlement.value = null
}

const resetForm = async () => {
  const userId = await loadCurrentUser()
  settlementForm.value = {
    id: null,
    investor_id: null,
    wallet_id: null,
    user_id: userId,
    amount: 0,
    amount_usdt: 0,
    rate: 0,
    direction: 'to_wallet',
    transaction_date: getTodayString(),
    description: ''
  }
  walletSearch.value = ''
}

const showError = (message: string) => {
  errorMessage.value = message
  showErrorDialog.value = true
}


const deleteSettlement = async (id: number) => {
  if (!confirm(t('settlements.areYouSureDelete'))) return
  try {
    const response = await apiDelete(`/api/settlements/${id}`)
    if (response.ok) {
      await loadSettlements()
    } else {
      showError(t('settlements.failedToDelete'))
    }
  } catch (error) {
    console.error('Delete settlement error:', error)
    showError(t('settlements.errorDeleting'))
  }
}

onMounted(async () => {
  await loadCurrentUser()
  loadSettlements()
  loadInvestors()
  loadWallets()
  loadUsers()
  // Set default period to today
  setDatePeriod('day')
})
</script>

<style scoped>
/* Sort icon visibility - hide by default, show on hover or when active */
.sortable-header .sort-icon {
  opacity: 0;
  transition: opacity 0.2s ease;
}

.sortable-header:hover .sort-icon,
.sortable-header.sort-active .sort-icon {
  opacity: 1;
}

.filters-wrapper {
  position: relative;
  margin-bottom: 36px;
}

.date-from-wrapper {
  position: relative;
  display: inline-block;
}

.period-buttons-container {
  position: absolute;
  top: 100%;
  right: 70px;
  margin-top: 4px;
  margin-bottom: 12px;
}

.period-buttons :deep(.v-btn) {
  margin: 0;
  padding: 4px 12px;
  min-width: auto;
  height: 24px;
  max-height: 24px;
}

.period-buttons :deep(.v-btn-toggle__content) {
  gap: 0;
}

.total-row {
  background-color: rgba(0, 0, 0, 0.05);
}

.no-border-table :deep(table) {
  border: none;
}
</style>
